import { Fragment, useCallback, useMemo, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import type { FileTreeNode } from "../file-tree";

type UseFileTreeSearchParams = {
  fileTree: Array<FileTreeNode>;
  onSelectFile: (path: string | null) => void;
};

type UseFileTreeSearchResult = {
  query: string;
  setQuery: (value: string) => void;
  isSearchActive: boolean;
  filteredTree: Array<FileTreeNode>;
  highlightMatches: (text: string) => ReactNode;
  handleSearchKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
};

const filterFileTree = (
  nodes: Array<FileTreeNode>,
  query: string,
): Array<FileTreeNode> => {
  const result: Array<FileTreeNode> = [];

  for (const node of nodes) {
    const nameMatches = node.name.toLowerCase().includes(query);
    const pathMatches = node.path.toLowerCase().includes(query);

    if (node.type === "directory") {
      const children = node.children ?? [];
      const filteredChildren = filterFileTree(children, query);
      const matchesQuery = nameMatches || pathMatches;
      const hasMatchingDescendants = filteredChildren.length > 0;

      if (!matchesQuery && !hasMatchingDescendants) {
        continue;
      }

      const nextChildren = hasMatchingDescendants
        ? filteredChildren
        : matchesQuery && children.length > 0
          ? children
          : undefined;

      result.push({
        ...node,
        children: nextChildren,
        matchesQuery,
        hasMatchingDescendants,
      });
      continue;
    }

    if (nameMatches || pathMatches) {
      result.push({
        ...node,
        matchesQuery: true,
      });
    }
  }

  return result;
};

const findFirstMatchingFilePath = (
  nodes: Array<FileTreeNode>,
): string | null => {
  const queue: Array<FileTreeNode> = [...nodes];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    if (current.type === "file") {
      if (current.matchesQuery) {
        return current.path;
      }
      continue;
    }

    if (!current.children?.length) {
      continue;
    }

    for (const child of current.children) {
      const shouldTraverse =
        child.type === "file"
          ? Boolean(child.matchesQuery)
          : Boolean(child.matchesQuery || child.hasMatchingDescendants);

      if (shouldTraverse) {
        queue.push(child);
      }
    }
  }

  return null;
};

export function useFileTreeSearch({
  fileTree,
  onSelectFile,
}: UseFileTreeSearchParams): UseFileTreeSearchResult {
  const [query, setQuery] = useState("");

  const normalizedQuery = query.trim().toLowerCase();
  const isSearchActive = normalizedQuery.length > 0;

  const filteredTree = useMemo(() => {
    if (!isSearchActive) {
      return fileTree;
    }

    return filterFileTree(fileTree, normalizedQuery);
  }, [fileTree, isSearchActive, normalizedQuery]);

  const highlightMatches = useCallback(
    (text: string): ReactNode => {
      if (!isSearchActive) {
        return text;
      }

      const lowerText = text.toLowerCase();
      const matchLength = normalizedQuery.length;

      let searchStart = 0;
      let matchIndex = lowerText.indexOf(normalizedQuery, searchStart);

      if (matchIndex === -1) {
        return text;
      }

      const segments: Array<ReactNode> = [];
      let segmentKey = 0;

      while (matchIndex !== -1) {
        if (matchIndex > searchStart) {
          segments.push(
            <Fragment key={`text-${segmentKey}`}>
              {text.slice(searchStart, matchIndex)}
            </Fragment>,
          );
          segmentKey += 1;
        }

        const matched = text.slice(matchIndex, matchIndex + matchLength);
        segments.push(
          <span
            key={`match-${segmentKey}`}
            className="text-primary font-medium"
          >
            {matched}
          </span>,
        );
        segmentKey += 1;

        searchStart = matchIndex + matchLength;
        matchIndex = lowerText.indexOf(normalizedQuery, searchStart);
      }

      if (searchStart < text.length) {
        segments.push(
          <Fragment key={`text-${segmentKey}`}>
            {text.slice(searchStart)}
          </Fragment>,
        );
      }

      return segments;
    },
    [isSearchActive, normalizedQuery],
  );

  const firstMatchingFilePath = useMemo(() => {
    if (!isSearchActive) {
      return null;
    }

    return findFirstMatchingFilePath(filteredTree);
  }, [filteredTree, isSearchActive]);

  const handleSearchKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        if (query) {
          event.preventDefault();
          setQuery("");
        } else {
          event.currentTarget.blur();
        }
        return;
      }

      if (event.key === "Enter" && firstMatchingFilePath) {
        event.preventDefault();
        onSelectFile(firstMatchingFilePath);
      }
    },
    [firstMatchingFilePath, onSelectFile, query],
  );

  return {
    query,
    setQuery,
    isSearchActive,
    filteredTree,
    highlightMatches,
    handleSearchKeyDown,
  };
}
